/** Navigation follows admitted recorded identities, never a pending card's tuple. */
import type { DrawerTarget } from '../ObjectDrawer'
import type { RecordTablePayload, RtPosition } from '../RecordTable'
import {
  recordHref,
  workbenchHref,
  subchainOverviewHref,
  subchainScope,
  type RecordContext,
  type ViewScope,
  type WorkCard,
} from './context'

export interface FundRelationships {
  fund_id: string
  scope: 'current_confirmed'
  scope_incomplete: boolean
  masters: {
    entity_id: string
    name: string | null
    legs: { position_id: string; holder_id: string; instrument_id: string }[]
    workspaces: {
      fund_id: string
      name: string
      own_counts: { proven: number; total: number; breaks: number }
    }[]
    below_context: { kind: 'below_entity'; fund_id: string; entity_id: string }
    terminal: boolean
  }[]
}

export interface ObjectContextChoice {
  id: string
  label: string
  overviewHref: string
  recordHref: string
}
export interface ObjectNavigation {
  choices: ObjectContextChoice[]
  below: { label: string; href: string } | null
  workspaces: {
    fundId: string
    label: string
    reference: string | null
    href: string
    ownCounts: { proven: number; total: number; breaks: number }
  }[]
  fundLike: boolean
}
interface Tuple {
  positionId: string
  holderId: string
  instrumentId: string
  issuerId: string
}
const recorded = (row: { state: string } | undefined): boolean =>
  row?.state === 'recorded' || row?.state === 'evidenced'

function idField(row: unknown, key: string): string | null {
  if (!row || typeof row !== 'object') return null
  const value = (row as Record<string, unknown>)[key]
  return typeof value === 'string' && value.length > 0 ? value : null
}

function actualTuples(table: RecordTablePayload): Tuple[] {
  const entities = new Map(table.entities.filter(recorded).map((row) => [row.id, row]))
  const instruments = new Map(table.instruments.filter(recorded).map((row) => [row.id, row]))
  const result: Tuple[] = []
  for (const position of table.positions.filter(recorded)) {
    const holder = idField(position, 'holder_id')
    const issuer = idField(position, 'issuer_id')
    const instrument = instruments.get(position.instrument_id)
    if (
      holder &&
      issuer &&
      entities.has(holder) &&
      entities.has(issuer) &&
      instrument &&
      idField(instrument, 'issuer_id') === issuer
    )
      result.push({
        positionId: position.id,
        holderId: holder,
        instrumentId: instrument.id,
        issuerId: issuer,
      })
  }
  return result
}

const tupleMatches = (tuple: Tuple, card: WorkCard): boolean =>
  card.identity.kind === 'subchain' &&
  card.identity.holder_id === tuple.holderId &&
  card.identity.instrument_id === tuple.instrumentId &&
  card.identity.issuer_id === tuple.issuerId &&
  card.members.some((member) => member.kind === 'position' && member.id === tuple.positionId)

function actualCard(card: WorkCard, tuples: Tuple[], table: RecordTablePayload): boolean {
  if (!card.identity) return false
  if (card.identity.kind === 'subchain') return tuples.some((tuple) => tupleMatches(tuple, card))
  if (card.identity.kind === 'entity')
    return table.entities.some((row) => row.id === card.identity.entity_id && recorded(row))
  return table.instruments.some(
    (row) =>
      row.id === card.identity.instrument_id &&
      recorded(row) &&
      idField(row, 'issuer_id') === card.identity.issuer_id &&
      table.entities.some((entity) => entity.id === card.identity.issuer_id && recorded(entity))
  )
}

function targetCards(
  table: RecordTablePayload,
  tuples: Tuple[],
  cards: WorkCard[],
  target: DrawerTarget
): WorkCard[] {
  if (target.type === 'entity') {
    if (!table.entities.some((row) => row.id === target.id && recorded(row))) return []
    return cards.filter(
      (card) =>
        card.identity.issuer_id === target.id ||
        (card.identity.kind === 'entity' && card.identity.entity_id === target.id)
    )
  }
  if (target.type === 'instrument') {
    if (!table.instruments.some((row) => row.id === target.id && recorded(row))) return []
    return cards.filter((card) => card.identity.instrument_id === target.id)
  }
  let position: RtPosition | undefined
  if (target.type === 'position') position = table.positions.find((row) => row.id === target.id)
  else {
    const trade = table.trades.find((row) => row.id === target.id && recorded(row))
    if (!trade) return []
    cards = cards.filter((card) =>
      card.members.some((member) => member.kind === 'trade' && member.id === trade.id)
    )
    if (trade.position_id) position = table.positions.find((row) => row.id === trade.position_id)
    else return cards.filter((card) => card.identity.instrument_id === trade.instrument_id)
  }
  if (!position || !recorded(position)) return []
  const positionId = position.id
  const tuple = tuples.find((row) => row.positionId === positionId)
  return tuple ? cards.filter((card) => tupleMatches(tuple, card)) : []
}

export function objectContextNavigation(
  value: RecordContext,
  target: DrawerTarget,
  scope: ViewScope,
  relationships?: FundRelationships
): ObjectNavigation {
  const empty: ObjectNavigation = { choices: [], below: null, workspaces: [], fundLike: false }
  if (!scope.fund_id || !value.fund_ids.includes(scope.fund_id)) return empty
  const records = value.records.filter((row) => row.fund_id === scope.fund_id)
  if (records.length !== 1) return empty
  const table = records[0].record
  const tuples = actualTuples(table)
  const cards = value.work.cards.filter(
    (card) => card.fund_ids.includes(scope.fund_id!) && actualCard(card, tuples, table)
  )
  const selected = targetCards(table, tuples, cards, target)
  const choices = [...new Map(selected.map((card) => [card.id, card])).values()].map((card) => ({
    id: card.id,
    label: card.label,
    overviewHref: subchainOverviewHref(card, scope),
    recordHref: recordHref(subchainScope(card, scope)),
  }))
  if (target.type !== 'entity') return { ...empty, choices }
  const entity = table.entities.find((row) => row.id === target.id && recorded(row))
  if (!entity) return { ...empty, choices }
  const reached = new Set(
    table.entities.filter((row) => row.is_fund_root && recorded(row)).map((row) => row.id)
  )
  let changed = true
  while (changed) {
    changed = false
    for (const tuple of tuples)
      if (reached.has(tuple.holderId) && !reached.has(tuple.issuerId)) {
        reached.add(tuple.issuerId)
        changed = true
      }
  }
  const relation =
    relationships?.fund_id === scope.fund_id && relationships.scope === 'current_confirmed'
      ? relationships.masters.find(
          (item) =>
            item.entity_id === target.id &&
            item.below_context.fund_id === scope.fund_id &&
            item.below_context.entity_id === target.id &&
            item.legs.some((leg) =>
              tuples.some(
                (tuple) =>
                  tuple.positionId === leg.position_id &&
                  tuple.holderId === leg.holder_id &&
                  tuple.instrumentId === leg.instrument_id &&
                  tuple.issuerId === target.id
              )
            )
        )
      : undefined
  const below =
    reached.has(target.id) && tuples.some((tuple) => tuple.holderId === target.id)
      ? {
          label: `View records below ${entity.name}`,
          href: recordHref({
            kind: 'fund',
            fund_id: scope.fund_id,
            client_id: scope.client_id,
            below_entity_id: target.id,
          }),
        }
      : null
  const duplicateNames = new Set(
    (relation?.workspaces ?? [])
      .filter((row, index, all) => all.some((other, n) => n !== index && other.name === row.name))
      .map((row) => row.name)
  )
  const workspaces = [
    ...new Map(
      (relation?.workspaces ?? [])
        .filter((row) => row.fund_id !== scope.fund_id)
        .map((row) => [row.fund_id, row])
    ).values(),
  ].map((row) => ({
    fundId: row.fund_id,
    label: row.name,
    reference: duplicateNames.has(row.name) ? row.fund_id : null,
    // A master can belong to another client; the feeder's folder hint is not authority.
    href: recordHref({ kind: 'fund', fund_id: row.fund_id }),
    ownCounts: row.own_counts,
  }))
  return {
    choices,
    below,
    workspaces,
    fundLike: entity.entity_nature === 'collective_vehicle' || !!relation,
  }
}

/** One exact admitted choice can carry its subchain; ambiguous choices keep the full fund work view. */
export function objectWorkContext(
  value: RecordContext,
  scope: ViewScope,
  navigation: ObjectNavigation
): { evidenceHref: string; chaseHref: string } | undefined {
  if (!scope.fund_id || !value.fund_ids.includes(scope.fund_id)) return undefined
  const choice = navigation.choices.length === 1 ? navigation.choices[0] : null
  const card = choice
    ? value.work.cards.find(
        (item) => item.id === choice.id && item.fund_ids.includes(scope.fund_id!)
      )
    : null
  const workScope: ViewScope = card
    ? subchainScope(card, scope)
    : {
        kind: 'fund',
        fund_id: scope.fund_id,
        client_id: scope.client_id,
      }
  return {
    evidenceHref: workbenchHref(workScope, 'evidence', card?.id),
    chaseHref: workbenchHref(workScope, 'chase', card?.id),
  }
}
